// frontend/src/components/common/FloatingCartBar.tsx
//
// Mobile-only floating "View cart · N items" pill (Zomato / Blinkit style).
// It shows up at the bottom of the screen as soon as the cart has items, with the
// thumbnails of the products, and slides up again every time something is added.
// On the product page it sits just above the fixed Add to Cart / Buy Now bar.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';

const GUEST_CART_KEY = 'guestCart';

// pages where the bar must never appear
const HIDDEN_PREFIXES = [
  '/cart',
  '/checkout',
  '/login',
  '/register',
  '/payment',
  '/forgot-password',
  '/reset-password',
  '/admin',
  '/employee',
];

interface CartThumb {
  id: number;
  name: string;
  image: string;
}

const getImageUrl = (imageUrl?: string): string => {
  if (!imageUrl) return '';
  if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) return imageUrl;
  const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace('/api', '');
  let c = imageUrl.replace(/^[./]+/, '');
  if (c.includes('uploads/')) {
    const m = c.match(/uploads\/(.+)$/);
    if (m) c = `uploads/${m[1]}`;
  } else {
    c = `uploads/${c}`;
  }
  return `${BASE}/${c}`.replace(/([^:]\/)\/+/g, '$1');
};

const firstImage = (product: any): string => {
  const imgs = product?.images;
  if (Array.isArray(imgs) && imgs.length > 0) {
    const first = imgs.find((i: any) => i?.is_primary) || imgs[0];
    return getImageUrl(first?.image_url || first?.url || '');
  }
  return getImageUrl(product?.image || product?.image_url || '');
};

const summarize = (items: any[]): { count: number; thumbs: CartThumb[] } => {
  const list = Array.isArray(items) ? items : [];
  const count = list.reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0);
  const thumbs: CartThumb[] = [];
  const seen = new Set<number>();
  for (const it of list) {
    const p = it?.product;
    const pid = Number(p?.id ?? it?.product_id);
    if (!pid || seen.has(pid)) continue;
    seen.add(pid);
    thumbs.push({ id: pid, name: p?.name || 'Product', image: firstImage(p) });
    if (thumbs.length >= 3) break;
  }
  return { count, thumbs };
};

const FloatingCartBar: React.FC = () => {
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [thumbs, setThumbs] = useState<CartThumb[]>([]);
  const [pulse, setPulse] = useState(0); // changes on every cart update -> re-runs the slide-up animation
  const lastCount = useRef(0);

  const loadCart = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      // Guest: the cart lives in localStorage
      if (!token) {
        const raw = localStorage.getItem(GUEST_CART_KEY);
        const cart = raw ? JSON.parse(raw) : null;
        const s = summarize(cart?.items || []);
        setCount(s.count);
        setThumbs(s.thumbs);
        return;
      }

      // Logged in: only customers have a cart
      let role = '';
      try {
        role = JSON.parse(localStorage.getItem('user') || 'null')?.role || '';
      } catch (e) {}
      if (role !== 'customer') {
        setCount(0);
        setThumbs([]);
        return;
      }

      const res: any = await apiService.getCart();
      if (res?.success && res?.data) {
        const s = summarize(res.data.items || []);
        setCount(s.count);
        setThumbs(s.thumbs);
      }
    } catch (e) {
      /* keep whatever is shown */
    }
  }, []);

  // initial load + every route change
  useEffect(() => {
    loadCart();
  }, [loadCart, location.pathname]);

  // refresh whenever something is added / removed anywhere in the site
  useEffect(() => {
    const onUpdate = () => {
      loadCart();
      setPulse(p => p + 1);
    };
    window.addEventListener('cartUpdated', onUpdate);
    window.addEventListener('focus', loadCart);
    return () => {
      window.removeEventListener('cartUpdated', onUpdate);
      window.removeEventListener('focus', loadCart);
    };
  }, [loadCart]);

  // slide up again when the number of items goes up
  useEffect(() => {
    if (count > lastCount.current) setPulse(p => p + 1);
    lastCount.current = count;
  }, [count]);

  const path = location.pathname;
  const hidden = HIDDEN_PREFIXES.some(p => path === p || path.startsWith(p + '/'));
  const onProductPage = /^\/products\/\d+/.test(path);
  const visible = count > 0 && !hidden;

  // leave room at the bottom of the page so the bar never covers the last content
  // (the product page already reserves room for its own fixed Add to Cart bar)
  useEffect(() => {
    if (visible && !onProductPage) {
      document.body.classList.add('fcb-has-bar');
    } else {
      document.body.classList.remove('fcb-has-bar');
    }
    return () => {
      document.body.classList.remove('fcb-has-bar');
    };
  }, [visible, onProductPage]);

  if (!visible) return null;

  return (
    <>
      <style>{`
        .fcb-wrap { display: none; }

        @media (max-width: 767px) {
          .fcb-wrap {
            display: flex;
            justify-content: center;
            position: fixed;
            left: 0;
            right: 0;
            bottom: calc(12px + env(safe-area-inset-bottom, 0px));
            z-index: 60;
            padding: 0 14px;
            pointer-events: none;
          }
          /* product page: sit above the fixed Add to Cart / Buy Now bar */
          .fcb-wrap.fcb-above-cta {
            bottom: calc(82px + env(safe-area-inset-bottom, 0px));
          }
          body.fcb-has-bar {
            padding-bottom: calc(68px + env(safe-area-inset-bottom, 0px));
          }
        }

        .fcb-bar {
          pointer-events: auto;
          display: flex;
          align-items: center;
          gap: 9px;
          width: 100%;
          max-width: 280px;
          padding: 6px 8px 6px 6px;
          border-radius: 999px;
          text-decoration: none;
          color: #fff;
          background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
          box-shadow: 0 10px 28px -6px rgba(139, 92, 246, 0.55), 0 2px 6px rgba(31, 23, 51, 0.18);
          font-family: 'Plus Jakarta Sans', sans-serif;
          animation: fcbSlideUp 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
          -webkit-tap-highlight-color: transparent;
        }
        .fcb-bar:active { transform: scale(0.985); }

        @keyframes fcbSlideUp {
          from { opacity: 0; transform: translateY(26px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .fcb-thumbs { display: flex; align-items: center; flex-shrink: 0; padding-left: 2px; }
        .fcb-thumb {
          width: 32px; height: 32px; border-radius: 50%;
          background: #fff; border: 2px solid #fff;
          overflow: hidden; flex-shrink: 0;
          box-shadow: 0 2px 6px rgba(31, 23, 51, 0.2);
        }
        .fcb-thumb + .fcb-thumb { margin-left: -12px; }
        .fcb-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .fcb-text { flex: 1; min-width: 0; display: flex; flex-direction: column; line-height: 1.2; }
        .fcb-title { font-size: 14px; font-weight: 800; letter-spacing: 0.1px; }
        .fcb-sub { font-size: 11px; font-weight: 500; opacity: 0.92; margin-top: 1px; }

        .fcb-go {
          width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: rgba(255, 255, 255, 0.22);
        }
        .fcb-go svg { width: 15px; height: 15px; color: #fff; }
      `}</style>

      <div className={`fcb-wrap${onProductPage ? ' fcb-above-cta' : ''}`}>
        {/* key={pulse} restarts the slide-up animation each time an item is added */}
        <Link key={pulse} to="/cart" className="fcb-bar" aria-label={`View cart, ${count} items`}>
          <div className="fcb-thumbs">
            {thumbs.map(t => (
              <div className="fcb-thumb" key={t.id}>
                {t.image ? (
                  <img
                    src={t.image}
                    alt={t.name}
                    onError={e => {
                      (e.target as HTMLImageElement).style.visibility = 'hidden';
                    }}
                  />
                ) : null}
              </div>
            ))}
          </div>
          <div className="fcb-text">
            <span className="fcb-title">View cart</span>
            <span className="fcb-sub">
              {count} {count === 1 ? 'item' : 'items'}
            </span>
          </div>
          <span className="fcb-go">
            <ChevronRightIcon />
          </span>
        </Link>
      </div>
    </>
  );
};

export default FloatingCartBar;